import {DEFAULT_SKU_SLUG} from '@packages/systems/commerce/constants';

import {getFieldBySlug} from '@packages/systems/dynamo/utils/CollectionUtils';
import {
  getSkuCollection,
  getProductCollection,
  getCategoriesCollection,
} from '@packages/systems/commerce/utils/CommerceCollectionUtils';
import {
  isItemRefSet,
  isImageRefSet,
  itemRefTargetCollectionId,
} from '@packages/systems/dynamo/utils/CollectionFieldUtils';

import {
  defineCollectionTypeReference,
  defineTypeReferenceForReferenceField,
  DynamoWrapperElementMatchers,
  createCollectionListQuerySpecification,
  createItemRefSetListQuerySpecification,
  createCollectionTemplatePageQuerySpecification,
} from '@packages/systems/cms/gateway/shared';

import {
  PRODUCTS_BINDING_CONTEXT_ID,
  PRODUCTS_TEMPLATE_BINDING_CONTEXT_ID,
  CATEGORIES_BINDING_CONTEXT_ID,
  CATEGORIES_TEMPLATE_BINDING_CONTEXT_ID,
  MORE_IMAGES_BINDING_CONTEXT_ID,
  MORE_IMAGES_BINDING_CONTEXT_TYPE_ID,
  fieldListBindingContextId,
} from './identifiers';

import {cartBindingContexts} from './cart';
import {addToCartBindingContext} from './addToCart';
import {checkoutPageRootContext} from './checkout-page';
import {paypalCheckoutPageRootContext} from './checkout-paypal-page';
import {orderConfirmationPageRootContext} from './order-confirmation-page';
import {orderBindingContexts} from './order';

import type {
  BindingContextDef,
  TypeReferenceBuilder,
} from '@packages/systems/dynamo/source-types';
import type {
  Collections,
  CollectionRecordType as Collection,
} from '@packages/systems/dynamo/records/CollectionRecord';
import type {FieldType as Field} from '@packages/systems/dynamo/records/Field';
import {
  getCollectionIdFromElement,
  getCollectionItemRefSetSlugFromElement,
  getBoundRepeaterFieldIdPathFromElement,
} from '@packages/systems/dynamo/utils/DynamoElementUtils';

export default function defineBindingContexts<RootTRef, EntityTRef, ListTRef>(
  collections: Collections,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  const products = getProductCollection(collections);
  const categories = getCategoriesCollection(collections);
  const skus = getSkuCollection(collections);

  const rootBindingContextDef = {
    variant: 'Root',
    label: 'Ecommerce',
    type: referenceBuilder.root(),
  } as const;

  const bindingContextDefs = [
    rootBindingContextDef,
    ...cartBindingContexts(referenceBuilder),
    checkoutPageRootContext(referenceBuilder),
    paypalCheckoutPageRootContext(referenceBuilder),
    orderConfirmationPageRootContext(referenceBuilder),
    ...orderBindingContexts(referenceBuilder),
  ];

  if (products && products._id && products.slug) {
    const productsId = products._id;
    const productsSlug = products.slug;
    const productsTypeReferenceDef = defineCollectionTypeReference(products);

    const productsListContextDef: BindingContextDef<
      RootTRef,
      EntityTRef,
      ListTRef
    > = {
      variant: 'List',
      id: PRODUCTS_BINDING_CONTEXT_ID,
      label: products.name,
      type: referenceBuilder.listOf(productsTypeReferenceDef),
      matchElement: DynamoWrapperElementMatchers.create((element) => {
        const boundCollectionId = getCollectionIdFromElement(element);
        const isItemRefSetList = Boolean(
          getCollectionItemRefSetSlugFromElement(element)
        );
        return !isItemRefSetList && boundCollectionId === productsId;
      }),
      connection: {
        _kind: 'LegacyDynamoWrapperConnection/Collection',
        id: productsId,
      },
      query: createCollectionListQuerySpecification(productsSlug, products),
    };

    const productsTemplateContextDef: BindingContextDef<
      RootTRef,
      EntityTRef,
      ListTRef
    > = {
      variant: 'Page',
      id: PRODUCTS_TEMPLATE_BINDING_CONTEXT_ID,
      label: products.name,
      type: referenceBuilder.entityOf(productsTypeReferenceDef),
      matchPage: (page) => page.dynCollection === productsId,
      query: createCollectionTemplatePageQuerySpecification(productsSlug),
    };

    bindingContextDefs.push(
      productsListContextDef,
      productsTemplateContextDef,
      ...defineItemRefSetFieldBindingContexts(products, referenceBuilder),
      ...defineMultiImageFieldBindingContexts(products, referenceBuilder),
      addToCartBindingContext(referenceBuilder)
    );
  }

  if (categories && categories._id && categories.slug) {
    const categoriesId = categories._id;
    const categoriesSlug = categories.slug;

    const categoriesTypeReferenceDef =
      defineCollectionTypeReference(categories);

    const categoriesListContextDef: BindingContextDef<
      RootTRef,
      EntityTRef,
      ListTRef
    > = {
      variant: 'List',
      id: CATEGORIES_BINDING_CONTEXT_ID,
      label: categories.name,
      type: referenceBuilder.listOf(categoriesTypeReferenceDef),
      matchElement: DynamoWrapperElementMatchers.create((element) => {
        const boundCollectionId = getCollectionIdFromElement(element);
        const isItemRefSetList = Boolean(
          getCollectionItemRefSetSlugFromElement(element)
        );
        return !isItemRefSetList && boundCollectionId === categoriesId;
      }),
      connection: {
        _kind: 'LegacyDynamoWrapperConnection/Collection',
        id: categoriesId,
      },
      query: createCollectionListQuerySpecification(categoriesSlug, categories),
    };

    const categoriesTemplateContextDef: BindingContextDef<
      RootTRef,
      EntityTRef,
      ListTRef
    > = {
      variant: 'Page',
      id: CATEGORIES_TEMPLATE_BINDING_CONTEXT_ID,
      label: categories.name,
      type: referenceBuilder.entityOf(categoriesTypeReferenceDef),
      matchPage: (page) => page.dynCollection === categoriesId,
      query: createCollectionTemplatePageQuerySpecification(categoriesSlug),
    };

    bindingContextDefs.push(
      categoriesListContextDef,
      categoriesTemplateContextDef,
      ...defineItemRefSetFieldBindingContexts(categories, referenceBuilder),
      ...defineMultiImageFieldBindingContexts(categories, referenceBuilder)
    );
  }

  const moreImagesField = skus ? getFieldBySlug(skus, 'more-images') : null;
  const defaultSkuField = products
    ? getFieldBySlug(products, DEFAULT_SKU_SLUG)
    : null;

  if (products && defaultSkuField && moreImagesField) {
    const moreImagesContext: BindingContextDef<RootTRef, EntityTRef, ListTRef> =
      {
        variant: 'List',
        id: MORE_IMAGES_BINDING_CONTEXT_ID,
        label: products.name,
        type: referenceBuilder.listOf({
          entity: 'Field',
          id: MORE_IMAGES_BINDING_CONTEXT_TYPE_ID,
        }),
        matchElement: DynamoWrapperElementMatchers.create((element) => {
          const bindingPath = getBoundRepeaterFieldIdPathFromElement(element);
          return (
            bindingPath.length > 1 &&
            bindingPath[bindingPath.length - 2] === defaultSkuField.id &&
            bindingPath[bindingPath.length - 1] === moreImagesField.id
          );
        }),
        connection: {
          _kind: 'LegacyDynamoWrapperConnection/ImageRefSet',
          path: [
            {
              id: defaultSkuField.id,
              slug: defaultSkuField.slug,
              type: defaultSkuField.type,
            },
            {
              id: moreImagesField.id,
              slug: moreImagesField.slug,
              type: moreImagesField.type,
              innerType: moreImagesField.innerType,
            },
          ],
        },
      };
    bindingContextDefs.push(moreImagesContext);
  }

  return bindingContextDefs;
}

function defineItemRefSetFieldBindingContexts<RootTRef, EntityTRef, ListTRef>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  return collection.fields.reduce<Array<any>>((contexts, field) => {
    if (isItemRefSet(field)) {
      const bindingContext = defineItemRefSetFieldBindingContext(
        field,
        collection,
        referenceBuilder
      );
      if (bindingContext) {
        contexts.push(bindingContext);
      }
    }
    return contexts;
  }, []);
}

function defineItemRefSetFieldBindingContext<RootTRef, EntityTRef, ListTRef>(
  field: Field,
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef> | null {
  const typeReferenceDef = defineTypeReferenceForReferenceField(field);

  if (!typeReferenceDef) {
    return null;
  }

  const collectionId = collection._id;
  const targetCollectionId = itemRefTargetCollectionId(field);

  if (!collectionId || !targetCollectionId) {
    return null;
  }

  return {
    variant: 'List',
    // TODO: Address field id uniqueness
    id: fieldListBindingContextId(field.id),
    label: field.name,
    type: referenceBuilder.listOf(typeReferenceDef),
    matchElement: DynamoWrapperElementMatchers.itemRefSetList(
      field.slug,
      targetCollectionId,
      collectionId
    ),
    connection: {
      _kind: 'LegacyDynamoWrapperConnection/ItemRefSet',
      id: targetCollectionId,
      itemRefSet: field.slug,
      contextCollectionId: collectionId,
    },
    query: createItemRefSetListQuerySpecification(field),
  };
}

/*
  Create BindingContext for each multi-image field of a collection
*/
function defineMultiImageFieldBindingContexts<RootTRef, EntityTRef, ListTRef>(
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef>[] {
  return collection.fields
    .toArray()
    .filter(isImageRefSet)
    .map((field) =>
      defineMultiImageFieldBindingContext(field, collection, referenceBuilder)
    );
}

function defineMultiImageFieldBindingContext<RootTRef, EntityTRef, ListTRef>(
  field: Field,
  collection: Collection,
  referenceBuilder: TypeReferenceBuilder<RootTRef, EntityTRef, ListTRef>
): BindingContextDef<RootTRef, EntityTRef, ListTRef> {
  return {
    variant: 'List',
    // TODO: Address field id uniqueness
    id: fieldListBindingContextId(field.id),
    label: collection.name,
    type: referenceBuilder.listOf({
      entity: 'Field',
      // TODO: Address field id uniqueness
      id: field.id,
    }),
    matchElement: DynamoWrapperElementMatchers.create((element) => {
      const bindingPath = getBoundRepeaterFieldIdPathFromElement(element);
      return (
        bindingPath.length > 0 &&
        bindingPath[bindingPath.length - 1] === field.id
      );
    }),
    connection: {
      _kind: 'LegacyDynamoWrapperConnection/ImageRefSet',
      path: [
        {
          id: field.id,
          slug: field.slug,
          type: field.type,
          innerType: field.innerType,
        },
      ],
    },
  };
}
